import React, { useContext, useReducer, useEffect, useState } from 'react'
import { Box, Card, Divider, Text } from 'pcln-design-system'
import { useRouter } from 'next/router'
import { findIndex, propEq } from 'ramda'
import styled from 'styled-components'
import Tabs from './Tabs'
import { HEADERS, PRODUCT_TABS } from './constants'
import reducer, { formPicker } from './reducer'
import BootstrapDataContext from '../../context/BootstrapDataContext'

import useSEMSearchHeadline from '../CustomHooks/useSEMSearchHeadline'
import CONTENT from '../../constants/content'
import useCityNameInFormHeadlineForSEM from '../CustomHooks/useCityNameInFormHeadlineForSEM'
import useContentfulSearchFormHeadlines from '../CustomHooks/useContentfulSearchFormHeadlines'
import { LOCATION_SEARCH_TYPE } from '../../types'

const StyledDivider = styled(Divider)`
  height: 0.25rem;
  padding: 0;
  width: ${100 / PRODUCT_TABS.length}%;
  background: ${props => props.theme.colors.blue};
  border: none;
  transition: 0.3s ease-in-out;
`

export default function SearchForm() {
  const { query } = useRouter()
  const { tab: currentTab } = query
  const {
    customer: { firstName },
    signInStatus: { signedIn, passiveSignIn }
  } = useContext(BootstrapDataContext)
  const isSignedIn = signedIn || passiveSignIn
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const VALUES = {
    hotels:
      useContentfulSearchFormHeadlines(
        'searchFormHotels',
        currentTab === 'hotels'
      ) || HEADERS.hotels,
    flights:
      useContentfulSearchFormHeadlines(
        'searchFormFlights',
        currentTab === 'flights'
      ) || HEADERS.flights,
    vacations:
      useContentfulSearchFormHeadlines(
        'searchFormVacations',
        currentTab === 'vacations'
      ) || HEADERS.vacations,
    cars:
      useContentfulSearchFormHeadlines(
        'searchFormCars',
        currentTab === 'cars'
      ) || HEADERS.cars,
    cruises:
      useContentfulSearchFormHeadlines(
        'searchFormCruises',
        currentTab === 'cruises'
      ) || HEADERS.cruises
  }

  const defaultHeadline =
    prePopHeadline || SEMHeadline || VALUES.hotels.headline
  const isVIP = isSignedIn && (currentTab === 'hotels' || !currentTab)
  /**
   * TODO: Add these values to contentful and add fallback values as well
   */
  const headlineVIP = firstName
    ? CONTENT.homepage.vip.headlineWithName.replace('{firstName}', firstName)
    : 'Welcome'
  const hotelHeadline = isSignedIn
    ? headlineVIP || VALUES.hotels.headline
    : defaultHeadline
  const hotelSub = CONTENT.homepage.hotel.signedInSubHeading
  const hotelSubheadline =
    isVIP && hotelSub ? hotelSub : VALUES.hotels.subheadline

  const [state, dispatch] = useReducer(
    reducer,
    formPicker(VALUES, currentTab, hotelHeadline, hotelSubheadline, setLocation)
  )

  useEffect(() => {
    dispatch({
      values: VALUES,
      type: currentTab,
      vipHead: hotelHeadline,
      vipSub: hotelSubheadline,
      setLocation
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, hotelHeadline, hotelSubheadline])

  const currentTabIndex =
    currentTab === undefined
      ? 0
      : findIndex(propEq('id', currentTab), PRODUCT_TABS)
  const marginLeft = `${(currentTabIndex * 100) / PRODUCT_TABS.length}%`

  return (
    <Card
      boxShadowSize="sm"
      borderWidth={0}
      borderRadius={6}
      width={[1, null, null, '70%']}
      maxWidth="852px"
      minWidth={[null, null, null, '720px']}
      color="background.lightest"
    >
      <Card
        boxShadowSize="md"
        borderWidth={0}
        borderRadius={6}
        color="background.light"
      >
        <Text
          p={3}
          pb={1}
          color="text"
          fontSize={[3, null, null, 4, 5]}
          fontWeight="bold"
          style={{ lineHeight: 1.25 }}
        >
          {state.headline}
        </Text>
        <Text
          px={3}
          color="text"
          pb={3}
          fontSize={[2, null, null, 3, 4]}
          style={{ lineHeight: 1.25 }}
        >
          {state.subheadline}
        </Text>
        <Tabs
          onClick={(name: string) => {
            dispatch({
              values: VALUES,
              type: name,
              vipHead: hotelHeadline,
              vipSub: hotelSubheadline,
              setLocation
            })
          }}
        />
        <StyledDivider ml={marginLeft} my={0} />
      </Card>
      <Box p={3} pb={1}>
        {state.component}
      </Box>
      <Divider m={0} />
      <Box p={3} color="background.light">
        {state.bumper}
      </Box>
    </Card>
  )
}
